import { Button } from 'shared-ui';

import type { PoiHistoryEvent } from '@/api/poiHistoryApi';
import { StatusPill } from '@/components/shared/StatusPill';
import { formatIsoDate } from '@/utils/dateUtils';
import { getPoiHistoryEventTitle } from './poiHistoryLabels';
import { getPoiHistoryEventPillPresentation } from './poiHistoryPillPresentation';

// Labels are Poppins Medium text-sm/leading-5; values Regular text-sm/leading-5 in
// muted-foreground (Poppins is the app-wide --font-sans, so no font utility needed).
function EventField({ label, value }: { label: string; value: string | null }) {
    return (
        <div className="space-y-0.5">
            <p className="text-foreground text-sm leading-5 font-medium">{label}</p>
            <p className="text-muted-foreground text-sm leading-5 font-normal">{value ?? '—'}</p>
        </div>
    );
}

interface PoiHistoryEventCardProps {
    event: PoiHistoryEvent;
    /** Opens the event's detail view in place of the panel tabs. */
    onViewDetails: (event: PoiHistoryEvent) => void;
}

/**
 * One event card on the POI history timeline: uppercase Poppins-Black title, the
 * recorded outcome as a status pill, then Date / Technician fields (em dash when
 * unrecorded) and the View Details action. Card box per design: rounded-md, 24px
 * padding, 16px gap between blocks.
 */
export function PoiHistoryEventCard({ event, onViewDetails }: PoiHistoryEventCardProps) {
    const title = getPoiHistoryEventTitle(event);
    const pill = getPoiHistoryEventPillPresentation(event);
    return (
        <article className="bg-card space-y-4 rounded-md border p-6" aria-label={title}>
            <h3 className="text-xl leading-7 font-black tracking-normal uppercase">{title}</h3>
            <div className="space-y-0.5">
                <p className="text-foreground text-sm leading-5 font-medium">Status</p>
                {pill ? (
                    <StatusPill presentation={pill} />
                ) : (
                    <p className="text-muted-foreground text-sm leading-5 font-normal">—</p>
                )}
            </div>
            <EventField label="Date" value={formatIsoDate(event.date)} />
            <EventField label="Technician" value={event.technician} />
            <Button
                type="button"
                className="h-10 w-full cursor-pointer"
                onClick={() => {
                    onViewDetails(event);
                }}
                aria-label={`View details for ${title}`}
            >
                View Details
            </Button>
        </article>
    );
}
